import * as moment from "moment";
import {LeadEventApiDto} from "../../../api/model/rest";
import AuthenticatedEntity from "../../user/AuthenticatedEntity";
import LeadStatusEvent from "./LeadStatusEvent";
import LeadQuoteFilesAttachedEvent from "./LeadQuoteFilesAttachedEvent";
import LeadBDPAssigneeChangedEvent from "./LeadBDPChangedEvent";
import {default as LeadAppointmentEvent} from "./LeadAppointmentEvent";
import LeadSmsSentEvent from "./LeadSmsSentEvent";
import LeadFilesAttachedEvent from "./LeadFilesAttachedEvent";
import LeadWatcherEvent from "./LeadWatcherEvent";
import LeadAssigneeEvent from "./LeadAssigneeEvent";
import LeadAppointmentStatusEvent from "./LeadAppointmentStatusEvent";
import LeadIncomingSmsEvent from "./LeadIncomingSmsEvent";
import LeadQuoteCommentCreatedEvent from "./LeadQuoteCommentCreatedEvent";
import LeadQuoteStatusChangedEvent from "./LeadQuoteStatusChangedEvent";
import LeadSurveyFilesAttachedEvent from "./LeadSurveyFilesAttachedEvent";
import LeadAgentChangedEvent from "./LeadAgentChangedEvent";
import LeadEmailSentEvent from "./LeadEmailSentEvent";
import Attachment from "../../attachment/Attachment";
import {Icon} from "../../../hiberUiKit/src/atoms/Icon/Icon";
import {IconColor} from "../../../hiberUiKit/src/molecules/StreamItem/StreamItemIcon";
import ActivityEvent from "../../event/ActivityEvent";
import LeadQuoteDeletedEvent from "./LeadQuoteDeletedEvent";
import LeadQuoteRestoredEvent from "./LeadQuoteRestoredEvent";
import LeadQuoteFinanceProductChangedEvent from "./LeadQuoteFinanceProductChangedEvent";

/**
 * Lead event model.
 */
export default class LeadEvent extends ActivityEvent {
	/**
	 * @param {string} id identifier of event.
	 * @param {string} leadId lead id.
	 * @param {LeadEventTypeEnum} type type of the event.
	 * @param {AuthenticatedEntity} entity entity which creates the event.
	 * @param {moment.Moment} creationDate creation date of event.
	 * @param {string} referenceNumber lead reference number.
	 */
	constructor(public readonly id: string,
		public readonly leadId: string,
		public readonly type: LeadEventTypeEnum,
		public readonly entity: AuthenticatedEntity,
		public readonly creationDate: moment.Moment,
		public readonly referenceNumber: string) {
		super();
	}

	getCssClass() {
		return '';
	}

	getIcon() {
		return Icon.New;
	}

	getIconColor(): IconColor {
		return IconColor.Yellow;
	}

	getAttachments(): Attachment[] {
		return [];
	}

	getActivityAdditionalMessages(): string[] {
		return [];
	}

	getActivityType(): string {
		return `LEAD`
	}

	getActivityMessage(): string {
		return `Lead ${this.type.toLowerCase()}`;
	}

	getCreationDate(): moment.Moment {
		return this.creationDate;
	}

	getId() {
		return this.id;
	}

	/**
	 * Get user display name.
	 */
	getUserDisplayName(): string {
		//TODO incoming sms isn't triggered by hiber user so we can't use user.
		if (this.type === LeadEventTypeEnum.IncomingSms) {
			return "";
		}
		else {
			return this.entity.getDisplayName();
		}
	}

	public static fromJson(json: LeadEventApiDto): LeadEvent {
		const type = json.type == LeadEventTypeEnum.Created ? LeadEventTypeEnum.Created : LeadEventTypeEnum.Updated;
		const entity = AuthenticatedEntity.fromJson(json.entity);
		return new LeadEvent(json.id, json.leadId.toString(), type, entity, moment(json.createdAt), json.referenceNumber);
	}
}

export enum LeadEventTypeEnum {
	StatusChanged = "STATUS_CHANGED",
	Created = "CREATED",
	Updated = "UPDATED",
	AppointmentCreated = "APPOINTMENT_CREATED",
	AppointmentUpdated = "APPOINTMENT_UPDATED",
	AppointmentStatusChanged = "APPOINTMENT_STATUS_CHANGED",
	AssigneesChanged = "ASSIGNEES_CHANGED",
	BdpAssigneeChanged = "LEAD_BUSINESS_DEVELOPMENT_PARTNER_ASSIGNED",
	AgentAssigneeChanged = "LEAD_AGENT_ASSIGNED",
	WatchersChanged = "WATCHERS_CHANGED",
	QuoteFilesAttached = "QUOTE_FILES_ATTACHED",
	LeadFilesAttached = "LEAD_FILES_ATTACHED",
	LeadFilesDeleted = "LEAD_FILES_DELETED",
	SmsSent = "SMS_SENT",
	EmailSent = "EMAIL_SENT",
	SurveyFilesAttached = "SURVEY_FILES_ATTACHED",
	IncomingSms = "SMS_RECEIVED",
	QuoteCommentCreated = "QUOTE_COMMENT_CREATED",
	QuoteStatusChanged = "QUOTE_STATUS_CHANGED",
	QuoteDeleted = "QUOTE_DELETED",
	QuoteRestored = "QUOTE_RESTORED",
	Note = "NOTE", //dummy type used for filtering
	NoteReminder = "REMINDER", //dummy type used for filtering
	ExternalCallRegistered = "EXTERNAL_CALL_REGISTERED",
	QuoteCreated = "QUOTE_CREATED", //dummy type used for last lead action
	NoteCreated = "NOTE_CREATED", //dummy type used for last lead action
	SurveyCreated = "SURVEY_CREATED", //dummy type used for last lead action
	SurveyUpdated = "SURVEY_UPDATED", //dummy type used for last lead action
	QuoteFinanceProductChanged = "QUOTE_FINANCE_PRODUCT_CHANGED"
}

export enum SmsEventSubtypeEnum {
	FreeTextContactSms = "FREE_TEXT_CONTACT_SMS",
	AppointmentConfirmationSms = "APPOINTMENT_CONFIRMATION_SMS",
	SeOnTheWaySms = "SE_ON_THE_WAY_SMS",
	SeReminderSms = "SE_REMINDER_SMS",
	CustomerReminderSms = "CUSTOMER_REMINDER_SMS"
}

export enum EmailEventSubtypeEnum {
	FreeTextContactEmail = "FREE_TEXT_CONTACT_EMAIL",
	QuoteEmail = "QUOTE_EMAIL"
}

/**
 * Contains all lead event types
 */
export type LeadEventType = LeadEvent
	| LeadAppointmentEvent
	| LeadAppointmentStatusEvent
	| LeadAssigneeEvent
	| LeadFilesAttachedEvent
	| LeadBDPAssigneeChangedEvent
	| LeadAgentChangedEvent
	| LeadIncomingSmsEvent
	| LeadQuoteCommentCreatedEvent
	| LeadQuoteFilesAttachedEvent
	| LeadQuoteStatusChangedEvent
	| LeadQuoteDeletedEvent
	| LeadQuoteRestoredEvent
	| LeadSmsSentEvent
	| LeadStatusEvent
	| LeadSurveyFilesAttachedEvent
	| LeadWatcherEvent
	| LeadEmailSentEvent
	| LeadQuoteFinanceProductChangedEvent;